import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, TextField, Grid, Card, CardContent, LinearProgress, Avatar, AvatarGroup, 
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel, 
  InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, ToggleButton, ToggleButtonGroup, Autocomplete, Divider, Grow 
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import WorkIcon from '@mui/icons-material/Work';
import Loading from '../components/Loading';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function LocationMarker({ position, setPosition }) {
  const map = useMapEvents({
    click(e) {
      setPosition(e.latlng);
      map.flyTo(e.latlng, map.getZoom());
    },
  });

  return position === null ? null : (
    <Marker position={position}></Marker>
  );
}

function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [newProject, setNewProject] = useState({ 
      name: '', quote_ref: '', start_date: '', due_date: '', client_id: '', country_id: '', division_id: '',
      description: '', progress: 0, priority: 'Medium', budget: 0, spent: 0,
      latitude: '', longitude: '', stage: 'Planning'
  });
  const [viewMode, setViewMode] = useState('list');
  const [clients, setClients] = useState([]);
  const [countries, setCountries] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [filter, setFilter] = useState('All');
  const [myProjects, setMyProjects] = useState(false);
  const [search, setSearch] = useState('');
  const [initialLoad, setInitialLoad] = useState(true);
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchProjects = async (isSearch = false) => {
    if (!isSearch) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      
      const res = await axios.get(`${API_URL}/projects?${params.toString()}`, { headers });
      setProjects(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      if (!isSearch) setLoading(false);
    }
  };

  const fetchMeta = async () => {
      try {
        const [cRes, coRes, dRes] = await Promise.all([
            axios.get(`${API_URL}/clients`, { headers }),
            axios.get(`${API_URL}/setup/countries`, { headers }),
            axios.get(`${API_URL}/setup/divisions`, { headers })
        ]);
        setClients(cRes.data);
        setCountries(coRes.data);
        setDivisions(dRes.data);
      } catch (e) {
          console.error(e);
      }
  }

  useEffect(() => {
    fetchMeta();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchProjects(!initialLoad);
      if (initialLoad) setInitialLoad(false);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  if (loading) return <Loading message="Loading Projects..." />;

  const handleCreate = async () => {
    setCreateLoading(true);
    try {
      const response = await axios.post(`${API_URL}/projects`, newProject, { headers });
      setOpen(false);
      navigate(`/projects/${response.data.id}`);
    } catch (err) {
      alert(err.response?.data?.message || 'Error creating project');
    } finally {
      setCreateLoading(false);
    }
  };

  const getRefColor = (ref) => {
      const palette = [
          { bg: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)', shadow: 'rgba(99,102,241,0.35)' },
          { bg: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)', shadow: 'rgba(14,165,233,0.35)' },
          { bg: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)', shadow: 'rgba(16,185,129,0.35)' },
          { bg: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', shadow: 'rgba(245,158,11,0.35)' },
          { bg: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)', shadow: 'rgba(239,68,68,0.35)' },
          { bg: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)', shadow: 'rgba(139,92,246,0.35)' },
          { bg: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)', shadow: 'rgba(236,72,153,0.35)' },
          { bg: 'linear-gradient(135deg, #14b8a6 0%, #2dd4bf 100%)', shadow: 'rgba(20,184,166,0.35)' },
      ];
      if (!ref) return palette[0];
      const short = ref.replace(/^ACTS-/i, '');
      const code = short.charCodeAt(0) || 0;
      return palette[code % palette.length];
  };

  const getStatusColor = (status) => {
      switch(status) {
          case 'Active': return 'primary';
          case 'In Progress': return 'primary';
          case 'Bid Stage': return 'info';
          case 'Completed': return 'success';
          case 'At Risk': return 'warning';
          case 'Overdue': return 'warning';
          case 'On Hold': return 'error';
          default: return 'default';
      }
  };

  const getPriorityColor = (priority) => {
      switch(priority) {
          case 'High': return '#DC3545';
          case 'Medium': return '#FFB020';
          case 'Low': return '#00C292';
          default: return '#6C757D';
      }
  };

  const loggedInFullName = localStorage.getItem('full_name') || localStorage.getItem('username');

  const filteredProjects = projects.filter(p => {
      // First check My Projects toggle
      if (myProjects) {
          const isMyProject = p.team && p.team.some(member => member.name === loggedInFullName);
          if (!isMyProject) return false;
      }
      
      // Then check Status filter
      if (filter === 'All') return true;
      if (filter === 'Active') return p.status === 'In Progress' || p.status === 'Active';
      if (filter === 'At Risk') return p.status === 'Overdue' || p.status === 'At Risk';
      return p.status === filter;
  });

  const handleExport = () => {
      if (filteredProjects.length === 0) return;
      
      const headers = ['Project Name', 'Reference', 'Client', 'Status', 'Due Date', 'Progress', 'Priority', 'Budget', 'Spent'];
      const csvRows = [headers.join(',')];

      filteredProjects.forEach(p => {
          const row = [
              `"${(p.name || '').replace(/"/g, '""')}"`,
              `"${(p.ref || '').replace(/"/g, '""')}"`,
              `"${(p.client || '').replace(/"/g, '""')}"`,
              `"${(p.status || '').replace(/"/g, '""')}"`,
              `"${(p.due_date || '').replace(/"/g, '""')}"`,
              `${Math.round(p.progress || 0)}%`,
              `"${(p.priority || '').replace(/"/g, '""')}"`,
              p.budget || 0,
              p.spent || 0
          ];
          csvRows.push(row.join(','));
      });

      const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `projects_export.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      
      {/* Modern Gradient Header */}
      <Grow in={true} timeout={500}>
        <Box sx={{ 
            mb: 4, 
            mt: 2, 
            p: 4, 
            borderRadius: 4, 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: 'white',
            boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 3
        }}>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                    <WorkIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                    Project Management
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Manage and track all company projects
                </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, position: 'relative', zIndex: 2 }}>
                <Button 
                    variant="contained" 
                    startIcon={<GetAppIcon />} 
                    onClick={handleExport}
                    disabled={filteredProjects.length === 0}
                    sx={{ 
                        borderRadius: 2, 
                        fontWeight: 'bold', 
                        px: 3, 
                        py: 1,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                    }}
                >
                    Export CSV
                </Button>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    onClick={() => setOpen(true)}
                    sx={{ 
                        borderRadius: 2, 
                        fontWeight: 'bold', 
                        px: 3, 
                        py: 1,
                        bgcolor: 'white',
                        color: '#1e3c72',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                        '&:hover': { bgcolor: '#f1f5f9', transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }
                    }}
                >
                    New Project
                </Button>
            </Box>
            
            {/* Decorative background circles */}
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
            <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        </Box>
      </Grow>

      {/* Filters and Controls */}
      <Card elevation={0} sx={{ mb: 4, p: 2, borderRadius: 4, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip
                    icon={<PersonIcon />}
                    label="My Projects"
                    onClick={() => setMyProjects(!myProjects)}
                    sx={{ 
                        fontWeight: 'bold', 
                        borderRadius: 2, 
                        px: 1,
                        bgcolor: myProjects ? '#eff6ff' : '#f8fafc',
                        color: myProjects ? '#2563eb' : '#64748b',
                        border: '1px solid',
                        borderColor: myProjects ? '#bfdbfe' : '#e2e8f0',
                        '&:hover': { bgcolor: myProjects ? '#dbeafe' : '#f1f5f9' },
                        transition: 'all 0.2s ease'
                    }}
                />
                <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 24 }} />
                {['All', 'Active', 'Bid Stage', 'Completed', 'At Risk', 'On Hold'].map(f => (
                    <Chip 
                        key={f} 
                        label={f} 
                        onClick={() => setFilter(f)} 
                        sx={{ 
                            fontWeight: '600', 
                            borderRadius: 2,
                            bgcolor: filter === f ? '#1e293b' : 'transparent',
                            color: filter === f ? '#ffffff' : '#64748b',
                            border: '1px solid',
                            borderColor: filter === f ? '#1e293b' : 'transparent',
                            '&:hover': { bgcolor: filter === f ? '#0f172a' : '#f1f5f9' },
                            transition: 'all 0.2s ease'
                        }}
                    />
                ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
                <TextField 
                    placeholder="Search projects..." 
                    size="small" 
                    value={search} 
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>
                    }}
                    sx={{ 
                        flexGrow: 1, 
                        minWidth: { sm: 250 },
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#f8fafc',
                            borderRadius: 2,
                            '& fieldset': { borderColor: '#e2e8f0' },
                            '&:hover fieldset': { borderColor: '#cbd5e1' },
                            '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '1px' },
                        }
                    }}
                />
                <Box sx={{ display: 'flex', gap: 2, height: '40px' }}>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }}
                  size="small"
                  sx={{ 
                      '& .MuiToggleButton-root': { 
                          color: '#94a3b8',
                          borderColor: '#e2e8f0',
                          '&.Mui-selected': { 
                              bgcolor: '#eff6ff', 
                              color: '#3b82f6',
                              borderColor: '#bfdbfe'
                          },
                          '&:hover': { bgcolor: '#f8fafc' }
                      }
                  }}
                >
                  <ToggleButton value="list"><ViewListIcon fontSize="small" /></ToggleButton>
                  <ToggleButton value="grid"><ViewModuleIcon fontSize="small" /></ToggleButton>
                </ToggleButtonGroup>
                </Box>
            </Box>
        </Box>
      </Card>

      {/* Project content */}
      {filteredProjects.length === 0 ? (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: '#f8fafc' }}>
              <FolderIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="h6" color="#64748b" fontWeight="700" mb={1}>No projects found</Typography>
              <Typography variant="body2" color="#94a3b8">Try adjusting your filters or create a new project to get started.</Typography>
          </Paper>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={3}>
            {filteredProjects.map(p => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>
                    <Card elevation={0} sx={{ 
                        height: '100%', 
                        borderRadius: 4, 
                        border: '1px solid #f1f5f9',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.03)', 
                        cursor: 'pointer', 
                        transition: 'all 0.3s ease', 
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 12px 28px rgba(0,0,0,0.08)', borderColor: '#cbd5e1' } 
                    }} onClick={() => navigate(`/projects/${p.id}`)}>
                        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
                                    <Avatar sx={{ 
                                        background: getRefColor(p.ref).bg,
                                        color: 'white', 
                                        width: 48, 
                                        height: 48, 
                                        fontWeight: '800',
                                        fontSize: '0.72rem',
                                        borderRadius: 2.5,
                                        boxShadow: `0 6px 16px ${getRefColor(p.ref).shadow}`,
                                        letterSpacing: '0.03em'
                                    }}>
                                        {p.ref ? p.ref.replace(/^ACTS-/i, '') : ''}
                                    </Avatar>
                                    <Box sx={{ overflow: 'hidden' }}>
                                        <Typography variant="caption" sx={{ fontWeight: '800', display: 'block', color: '#3b82f6', letterSpacing: '0.05em' }}>{p.ref}</Typography>
                                        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: '800', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</Typography>
                                    </Box>
                                </Box>
                                <Chip 
                                    label={p.status} 
                                    size="small" 
                                    color={getStatusColor(p.status)} 
                                    sx={{ height: 24, fontSize: '0.75rem', fontWeight: 'bold', borderRadius: 1.5 }} 
                                />
                            </Box>
                            
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <DescriptionIcon sx={{ fontSize: 16, color: '#94a3b8' }} /> {p.client}
                                </Typography>
                                {p.quote_ref && (
                                    <Typography variant="body2" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 16, display: 'flex', justifyContent: 'center' }}>•</Box> Quote: {p.quote_ref}
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ mt: 'auto' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getPriorityColor(p.priority) }} />
                                        <Typography variant="caption" fontWeight="600" color="#64748b">{p.priority}</Typography>
                                    </Box>
                                    <Typography variant="caption" fontWeight="600" color="#64748b">Due: {p.due_date || 'N/A'}</Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="caption" fontWeight="bold" color="#64748b">Progress</Typography>
                                    <Typography variant="caption" fontWeight="bold" color="#1e293b">{p.progress}%</Typography>
                                </Box>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={p.progress} 
                                    sx={{ 
                                        height: 8, 
                                        borderRadius: 4, 
                                        bgcolor: '#f1f5f9',
                                        mb: 3,
                                        '& .MuiLinearProgress-bar': { borderRadius: 4, backgroundImage: 'linear-gradient(45deg, #3b82f6 0%, #60a5fa 100%)' }
                                    }} 
                                />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px dashed #e2e8f0' }}>
                                    <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 32, height: 32, fontSize: '0.8rem', borderColor: 'white', borderWidth: 2 } }}>
                                        {p.team && p.team.map((member, idx) => (
                                            <Avatar key={idx} alt={member.name} sx={{ bgcolor: '#eff6ff', color: '#3b82f6', fontWeight: 'bold' }}>
                                                {member.name ? member.name.charAt(0) : '?'}
                                            </Avatar>
                                        ))}
                                    </AvatarGroup>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography variant="body2" display="block" fontWeight="800" color="#1e293b">${p.spent?.toLocaleString() || 0}</Typography>
                                        <Typography variant="caption" fontWeight="600" color="#94a3b8">of ${p.budget?.toLocaleString() || 0}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
      ) : (
        <Grow in={true} timeout={1200}>
        <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: 'none', boxShadow: '0 10px 40px rgba(0,0,0,0.06)', width: '100%', bgcolor: 'white' }}>
            <Box sx={{ overflowX: 'auto', width: '100%', display: 'block' }}>
            <Table sx={{ width: '100%', tableLayout: 'auto', '& .MuiTableCell-root': { borderColor: '#f0f0f0', py: 2.5 } }} aria-label="projects table">
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Project / Reference</TableCell>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Client</TableCell>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Due Date</TableCell>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Progress</TableCell>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Priority</TableCell>
                        <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Financials</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredProjects.map((p) => (
                        <TableRow
                            key={p.id}
                            hover
                            sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer', transition: 'background-color 0.2s, transform 0.1s', '&:hover': { bgcolor: '#f8fbff !important' } }}
                            onClick={() => navigate(`/projects/${p.id}`)}
                        >
                            <TableCell component="th" scope="row">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                    <Avatar sx={{ 
                                        background: getRefColor(p.ref).bg,
                                        color: 'white', 
                                        fontWeight: '900', 
                                        width: 46, height: 46, 
                                        fontSize: '0.68rem',
                                        borderRadius: 2.5,
                                        boxShadow: `0 6px 16px ${getRefColor(p.ref).shadow}`,
                                        letterSpacing: '0.03em'
                                    }}>
                                        {p.ref ? p.ref.replace(/^ACTS-/i, '') : ''}
                                    </Avatar>
                                    <Box>
                                        <Typography fontWeight="700" sx={{ color: '#1e293b', fontSize: '0.95rem' }}>{p.name}</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                            <Typography variant="caption" sx={{ fontWeight: '800', color: '#3b82f6', bgcolor: '#eff6ff', px: 1, py: 0.25, borderRadius: 1.5 }}>{p.ref}</Typography>
                                            {p.quote_ref && <Typography variant="caption" fontWeight="600" color="#94a3b8">Quote: {p.quote_ref}</Typography>}
                                        </Box>
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell sx={{ color: '#334155', fontWeight: 600, fontSize: '0.85rem' }}>{p.client}</TableCell>
                            <TableCell>
                                <Chip label={p.status} size="small" color={getStatusColor(p.status)} sx={{ height: 26, fontSize: '0.75rem', fontWeight: 'bold', borderRadius: '6px', px: 1 }} />
                            </TableCell>
                            <TableCell sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.85rem' }}>{p.due_date || '—'}</TableCell>
                            <TableCell sx={{ minWidth: 150 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ width: '100%', mr: 1.5 }}>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={p.progress} 
                                            sx={{ 
                                                height: 8, 
                                                borderRadius: 4, 
                                                bgcolor: '#f1f5f9',
                                                '& .MuiLinearProgress-bar': { borderRadius: 4, backgroundImage: 'linear-gradient(45deg, #3b82f6 0%, #60a5fa 100%)' }
                                            }} 
                                        />
                                    </Box>
                                    <Box sx={{ minWidth: 40 }}>
                                        <Typography variant="caption" fontWeight="800" color="#1e293b">{`${Math.round(p.progress)}%`}</Typography>
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getPriorityColor(p.priority) }} />
                                    <Typography variant="body2" fontWeight="700" color="#475569">{p.priority}</Typography>
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" fontWeight="800" color="#1e293b">${p.budget?.toLocaleString() || 0}</Typography>
                                <Typography variant="caption" fontWeight="600" color="#94a3b8">Spent: ${p.spent?.toLocaleString() || 0}</Typography>
                            </TableCell>
                        </TableRow>
                    ))}
                    {filteredProjects.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 6, color: '#94a3b8', fontWeight: 600 }}>
                                No projects found matching your criteria.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            </Box>
        </Paper>
        </Grow>
      )}

      {/* Add Project Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
        <DialogTitle sx={{ bgcolor: '#1e3c72', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Add New Project
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'white' }}>
                <CloseIcon />
            </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                
                {/* Basic Info */}
                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                    <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Basic Information</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Name" fullWidth value={newProject.name} onChange={e => setNewProject({...newProject, name: e.target.value})} variant="outlined" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Quote Ref" fullWidth value={newProject.quote_ref} onChange={e => setNewProject({...newProject, quote_ref: e.target.value})} variant="outlined" />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Description" multiline rows={3} fullWidth value={newProject.description} onChange={e => setNewProject({...newProject, description: e.target.value})} />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Timeline & Status */}
                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                    <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Timeline & Status</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Start Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={newProject.start_date} onChange={e => setNewProject({...newProject, start_date: e.target.value})} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Due Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={newProject.due_date} onChange={e => setNewProject({...newProject, due_date: e.target.value})} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Priority</InputLabel>
                                <Select value={newProject.priority} label="Priority" onChange={e => setNewProject({...newProject, priority: e.target.value})}>
                                    <MenuItem value="High">High</MenuItem>
                                    <MenuItem value="Medium">Medium</MenuItem>
                                    <MenuItem value="Low">Low</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Stage</InputLabel>
                                <Select value={newProject.stage} label="Stage" onChange={e => setNewProject({...newProject, stage: e.target.value})}>
                                    <MenuItem value="Planning">Planning</MenuItem>
                                    <MenuItem value="Site Work">Site Work</MenuItem>
                                    <MenuItem value="Reporting">Reporting</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Classification */}
                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                    <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Classification & Location</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={clients}
                                getOptionLabel={(option) => option.name || ''}
                                value={clients.find(c => c.id === newProject.client_id) || null}
                                onChange={(event, newValue) => {
                                    setNewProject({...newProject, client_id: newValue ? newValue.id : ''});
                                }}
                                renderInput={(params) => <TextField {...params} label="Client" fullWidth />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Country</InputLabel>
                                <Select value={newProject.country_id} label="Country" onChange={e => setNewProject({...newProject, country_id: e.target.value})}>
                                    {countries.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Division</InputLabel>
                                <Select value={newProject.division_id} label="Division" onChange={e => setNewProject({...newProject, division_id: e.target.value})}>
                                    {divisions.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#334155' }}>Project Location</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#94a3b8' }}>Tap map to pin location</Typography>
                            </Box>
                            <Box sx={{ height: 260, width: '100%', mt: 1.5, borderRadius: 3, overflow: 'hidden', border: '2px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                                <MapContainer center={[25, 45]} zoom={4} style={{ height: '100%', width: '100%' }}>
                                    <TileLayer
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        attribution='&copy; OpenStreetMap'
                                    />
                                    <LocationMarker 
                                        position={newProject.latitude && newProject.longitude ? { lat: newProject.latitude, lng: newProject.longitude } : null} 
                                        setPosition={(latlng) => setNewProject({ ...newProject, latitude: latlng.lat, longitude: latlng.lng })}
                                    />
                                </MapContainer>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

            </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
            <Button onClick={() => setOpen(false)} sx={{ color: '#64748b', fontWeight: 'bold' }}>Cancel</Button>
            <Button 
                onClick={handleCreate} 
                variant="contained" 
                disabled={createLoading}
                sx={{ 
                    borderRadius: 2, 
                    fontWeight: 'bold', 
                    px: 3, 
                    py: 1,
                    background: 'linear-gradient(45deg, #1e3c72 0%, #2a5298 100%)',
                    boxShadow: '0 4px 12px rgba(30,60,114,0.3)'
                }}
            >
              {createLoading ? 'Creating...' : 'Create Project'}
            </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Projects;
